import { Text } from "@mantine/core"
import { PersonName } from "@components/PersonName"
import { ContractTitle } from "@/-components/ContractTitle"
import { ActivityTitle } from "@components/ActivityTitle"
import { ProductionTitle } from "@components/ProductionTitle"
import { Link, NavigateOptions } from "@tanstack/react-router"
import { CastTitle } from "@components/CastTitle"

type ReminderTo =
  | "/app/contacts/$id"
  | "/app/art/activities/$id"
  | "/app/art/activities/$id"
  | "/app/contracts/contracts/$id"
  | "/app/art/productions/$id"

type ReminderConfig = {
  component: (id: number) => React.ReactNode
  to: ReminderTo
}

const reminderConfigs: Record<number, ReminderConfig> = {
  1: {
    component: (id) => <PersonName personId={id} />,
    to: "/app/contacts/$id",
  },
  2: {
    component: (id) => <ActivityTitle activityId={id} />,
    to: "/app/art/activities/$id",
  },
  3: {
    component: (id) => <CastTitle castId={id} />,
    to: "/app/art/activities/$id",
  },
  4: {
    component: (id) => <ContractTitle contractId={id} />,
    to: "/app/contracts/contracts/$id",
  },
  5: {
    component: (id) => <ProductionTitle productionId={id} />,
    to: "/app/art/productions/$id",
  },
}

function buildLinkOptions(
  to: ReminderTo,
  id: number,
  reminderId?: number,
  objectTypeId?: number,
  activityIdForCast?: number
): NavigateOptions {
  return {
    to,
    search: {
      tab: objectTypeId === 3 ? "casts" : "start",
      reminderDetailOpened: true,
      reminderId,
    },
    params: {
      id:
        objectTypeId === 3 && activityIdForCast
          ? activityIdForCast.toString()
          : id.toString(),
    },
  }
}

export function getReminderLinkOptions(
  objectTypeId: number,
  objectId: number,
  reminderId?: number,
  activityIdForCast?: number
): NavigateOptions | null {
  const config = reminderConfigs[objectTypeId]

  if (!config) {
    return null
  }

  return buildLinkOptions(
    config.to,
    objectId,
    reminderId,
    objectTypeId,
    activityIdForCast
  )
}

function useGetReminderTitleComponent(
  objectTypeId: number,
  objectId: number,
  reminderId?: number,
  options?: {
    withLink?: boolean
    linkColor?: string
    activityIdForCast?: number
  }
) {
  const config = reminderConfigs[objectTypeId]
  const withLink = options?.withLink ?? true
  const linkColor = options?.linkColor ?? "var(--mantine-color-blue-6)"
  const activityIdForCast = options?.activityIdForCast

  const ReminderTitleComponent = () => {
    if (config) {
      if (!withLink) {
        return config.component(objectId)
      }

      return (
        <Link
          {...buildLinkOptions(
            config.to,
            objectId,
            reminderId,
            objectTypeId,
            activityIdForCast
          )}
          style={{
            textDecoration: "none",
            color: linkColor,
          }}
        >
          {config.component(objectId)}
        </Link>
      )
    }
    return (
      <Text>
        Object Type ID: {objectTypeId}, Object ID: {objectId}
      </Text>
    )
  }

  return ReminderTitleComponent
}

export default useGetReminderTitleComponent
